import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Typography, Button, Card, CardActionArea, CardContent, Chip, Alert, CircularProgress } from '@mui/material'
import { pieceBadges, thumbnailFor, imageUrl, processNowMessage } from './reviewModel'
import { processNow } from './photoReviewApi'
import ReopenDialog from './ReopenDialog'

const CHECKER = {
    backgroundColor: '#fff',
    backgroundImage: 'linear-gradient(45deg,#ddd 25%,transparent 25%),linear-gradient(-45deg,#ddd 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#ddd 75%),linear-gradient(-45deg,transparent 75%,#ddd 75%)',
    backgroundSize: '12px 12px',
    backgroundPosition: '0 0,0 6px,6px -6px,-6px 0',
}

export default function ReviewGrid({ items, fetchResource, isPending, onChanged, onReopened }) {
    const { t } = useTranslation()
    const [message, setMessage] = useState(null)
    const [reopening, setReopening] = useState(false)

    async function handleProcessNow() {
        const response = await processNow(fetchResource)
        if (response.error) return
        setMessage(processNowMessage(response))
        onChanged()
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 4 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ flex: 1 }}>{t('photoReview.grid.count', { count: items.length })}</Typography>
                {/* A live piece back to review, by barcode (photoPipeline/reopen.js). */}
                <Button variant='outlined' onClick={() => setReopening(true)}>{t('photoReview.reopen.button')}</Button>
                <Button variant='outlined' onClick={handleProcessNow} disabled={isPending('processNow')} startIcon={isPending('processNow') ? <CircularProgress size={16} /> : null}>
                    {t('photoReview.processNow.button')}
                </Button>
            </Box>
            {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{t(message.key, message.values)}</Alert>}
            {items.length === 0 && <Typography color='text.secondary'>{t('photoReview.grid.empty')}</Typography>}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                {items.map(item => {
                    const badges = pieceBadges(item)
                    const thumb = thumbnailFor(item)
                    return (
                        <Card key={item.sku} variant='outlined'>
                            <CardActionArea component={RouterLink} to={`/photo-review/${item.sku}`}>
                                <Box sx={{ aspectRatio: '1', ...CHECKER }}>
                                    {thumb && <img src={imageUrl(thumb.photoId, thumb.variant, thumb.version, 'preview')} alt={t('photoReview.grid.thumbnailAlt', { sku: item.sku })} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />}
                                </Box>
                                <CardContent sx={{ p: 1 }}>
                                    <Typography sx={{ fontWeight: 600 }}>{item.sku}</Typography>
                                    {item.category && <Typography variant='body2' color='text.secondary'>{item.category}</Typography>}
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                        {item.republish && <Chip size='small' color='warning' label={t('photoReview.republish.chip')} />}
                                        <Chip size='small' color='success' variant='outlined' label={t('photoReview.grid.approved', { approved: badges.approved, total: badges.total })} />
                                        {badges.ready > 0 && <Chip size='small' color='primary' label={t('photoReview.grid.ready', { count: badges.ready })} />}
                                        {badges.editing > 0 && <Chip size='small' color='info' icon={<CircularProgress size={12} color='inherit' />} label={t('photoReview.grid.editing', { count: badges.editing })} />}
                                        {badges.queued > 0 && <Chip size='small' color='info' variant='outlined' label={t('photoReview.grid.queued', { count: badges.queued })} />}
                                        {badges.waiting > 0 && <Chip size='small' variant='outlined' label={t('photoReview.grid.waiting', { count: badges.waiting })} />}
                                        {badges.batching > 0 && <Chip size='small' label={t('photoReview.grid.batching', { count: badges.batching })} />}
                                        {badges.failed > 0 && <Chip size='small' color='error' label={t('photoReview.grid.failed', { count: badges.failed })} />}
                                        {badges.rejected > 0 && <Chip size='small' color='error' label={t('photoReview.grid.rejected', { count: badges.rejected })} />}
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    )
                })}
            </Box>
            {reopening && (
                <ReopenDialog
                    open
                    fetchResource={fetchResource}
                    onClose={() => setReopening(false)}
                    onReopened={sku => { setReopening(false); onReopened(sku) }}
                />
            )}
        </Box>
    )
}
